import { useCallback, useState } from 'react'
import { connectorSyncsOnDemand } from '@app/shared'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  courseGrade,
  courseSlug,
  datedAssignments,
  gradeChipLabel,
  minutesToClock,
  relativeDay,
  type Course,
  type ISODate,
} from '@app/shared'
import clsx from 'clsx'

import {
  useAnnouncements,
  useAssignments,
  useClock,
  useCourses,
  useSyncConnector,
  useTerm,
} from '../../lib/data'
import {
  agoPhrase,
  hueBg,
  nextMeeting,
  registerCourseCodes,
  runwayGroups,
  runwaySummary,
} from '../../components/school/derive'
import { localDayOf, timeRemaining } from '../../lib/time'
import { MChevronRight, MMic } from '../shell/icons'
import {
  Band,
  BandHeading,
  Card,
  Chip,
  ChipRow,
  Eyebrow,
  SCROLL_BOTTOM,
  Skeleton,
} from '../shell/parts'
import { PullToRefresh } from '../sync/PullToRefresh'
import { ConsequenceBanner, StillCurrentRule, SyncFooter, ThrottleStrip, useSyncHonesty } from '../sync/SyncReport'

// ============================================================================
// 1e · SCHOOL — docs/design/handoff-mobile/README.md §1e.
//
// "What is due, then what the courses are." The runway comes first because it is
// the reason to open this screen at all; the course list is what you go to
// second. That order is the design and it is not negotiable on a 412px column.
//
// ── TWO THINGS THE MOCKUP DRAWS THAT ARE NOT HERE, both for the same stated
// rule ("anything the upstream API cannot supply is absent from the UI, not
// shown as an empty affordance"):
//   · The 40px SEARCH TILE in the band. Search is one of the five screens
//     deliberately absent from mobile, so the tile's only possible destination is
//     a screen that says it does not exist here. A button whose outcome is "not
//     on this device" is an empty affordance.
//   · WAITLIST POSITION anywhere — the README says so itself: "the Canvas API
//     cannot supply it."
// Both named in the port report.
//
// THE ROSTER IS WHATEVER THE API RETURNS. The mockup shows four courses and the
// README's Content section names four; production carries five. Nothing here
// counts to four.
// ============================================================================

type Tab = 'runway' | 'courses' | 'announcements'

export function MobileSchool() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { data: clock } = useClock()
  const { data: term } = useTerm()
  const { data: courses, isPending: coursesPending, error: coursesError } = useCourses()
  // Every course gets its own colour — see components/school/derive.ts. Idempotent; the desktop
  // overview registers the same roster the same way, so whichever loads first wins nothing.
  if (courses) registerCourseCodes(courses.map((c) => c.code))
  // OPEN WORK ONLY, the same list the desktop overview reads. Without the filter every graded
  // assignment whose deadline had passed came back and the runway pinned it as OVERDUE — ten of
  // them on the owner's phone on 2026-09-18, all finished in bCourses.
  const { data: assignments, isPending: assignmentsPending } = useAssignments({ openOnly: true })
  const { data: announcements } = useAnnouncements()
  const sync = useSyncConnector()
  const honesty = useSyncHonesty(clock?.date, clock?.minutesIntoDay ?? 0)

  const [tab, setTab] = useState<Tab>('runway')

  const refresh = useCallback(async () => {
    const live = (honesty.connectors ?? []).filter(connectorSyncsOnDemand)
    await Promise.allSettled(live.map((c) => sync.mutateAsync(c.id)))
    await queryClient.invalidateQueries()
  }, [honesty.connectors, queryClient, sync])

  const today = clock?.date
  const now = clock?.minutesIntoDay ?? 0
  const summary = today ? runwaySummary(assignments ?? [], today, now) : null
  const unread = (announcements ?? []).filter((a) => !a.read).length
  const twoWeeks = today
    ? datedAssignments(assignments).filter((a) => {
        const t = timeRemaining(a.dueAt, today, now)
        return t.minutes >= 0 && t.minutes <= 14 * 1440
      }).length
    : 0

  return (
    <>
      <Band band="school">
        <BandHeading
          title="School"
          subline={
            <span className="truncate">
              {(courses?.length ?? 0)} {courses?.length === 1 ? 'course' : 'courses'} ·{' '}
              {twoWeeks} {twoWeeks === 1 ? 'deadline' : 'deadlines'} in two weeks
              {unread > 0 && ` · ${unread} unread`}
            </span>
          }
        />
      </Band>

      <ChipRow>
        <Chip active={tab === 'runway'} onClick={() => setTab('runway')}>
          Runway
        </Chip>
        <Chip active={tab === 'courses'} onClick={() => setTab('courses')}>
          Courses
        </Chip>
        <Chip
          active={tab === 'announcements'}
          count={unread}
          onClick={() => setTab('announcements')}
        >
          Announcements
        </Chip>
      </ChipRow>

      <PullToRefresh connectors={honesty.connectors} onRefresh={refresh}>
        <div className="flex flex-col gap-[12px] px-[14px]" style={{ paddingBottom: SCROLL_BOTTOM }}>
          {honesty.broken.map((c) => (
            <ConsequenceBanner
              key={c.id}
              connector={c}
              busy={sync.isPending}
              onRetry={(id) => sync.mutate(id)}
              error={sync.error}
            />
          ))}
          {honesty.throttled.map((c) => (
            <ThrottleStrip key={c.id} connector={c} />
          ))}
          {(honesty.broken.length > 0 || honesty.throttled.length > 0) && <StillCurrentRule />}

          {tab === 'runway' && (
            <RunwayCard
              today={today}
              nowMinutes={now}
              assignments={assignments}
              isPending={assignmentsPending}
              staleFrom={honesty.staleFrom}
              overdue={summary?.overdue ?? 0}
            />
          )}

          {/* RECORDINGS — the phone's way into starting one, watching the queue and reading the
              history back (REC-1c). It sits above the course list on both tabs because it is a
              THING TO DO on the way into a lecture, and the course list is a thing to browse. */}
          {(tab === 'runway' || tab === 'courses') && (
            <button
              type="button"
              onClick={() => navigate('/school/recordings')}
              className="flex flex-none items-center gap-3 rounded-card border border-line bg-surface px-[14px] py-3 text-left active:bg-surface-2"
            >
              <MMic className="flex-none text-ink-2" />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[14.5px] font-semibold leading-[1.1] text-ink">
                  Recordings
                </span>
                <span className="mt-[5px] block truncate text-[12px] font-normal leading-[1.4] text-ink-3">
                  Record a lecture, or see what is on its way
                </span>
              </span>
              <MChevronRight className="flex-none text-faintest" />
            </button>
          )}

          {(tab === 'runway' || tab === 'courses') && (
            <div className="flex flex-none flex-col gap-[9px]">
              <Eyebrow className="pl-[2px] text-microcopy">Courses</Eyebrow>
              {coursesPending && <Skeleton height={62} className="rounded-card" />}
              {coursesError && (
                <p className="text-[12px] leading-[1.5] text-microcopy text-pretty">
                  The course list could not be read, so none is shown.{' '}
                  {coursesError instanceof Error ? coursesError.message : ''}
                </p>
              )}
              {courses?.map((course) => (
                <CourseRow
                  key={course.id}
                  course={course}
                  today={today}
                  nowMinutes={now}
                  term={term}
                  onOpen={() => navigate(`/school/${courseSlug(course.code)}`)}
                />
              ))}
            </div>
          )}

          {tab === 'announcements' && (
            <Card className="gap-3">
              {(announcements ?? []).length === 0 && (
                <p className="text-[11.5px] leading-[1.4] text-microcopy">
                  No announcements have come down the connector.
                </p>
              )}
              {(announcements ?? []).slice(0, 12).map((a) => (
                <div key={a.id} className="flex gap-[10px]">
                  <span
                    className={clsx(
                      'mt-[6px] size-[7px] flex-none rounded-full',
                      a.read ? 'bg-dash' : 'bg-danger-dot',
                    )}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-[14px] font-semibold leading-[1.25] text-ink text-pretty">
                      {a.title}
                    </div>
                    <div className="mt-[5px] text-[11px] font-normal leading-none text-muted">
                      {a.courseCode}
                      {a.author && ` · ${a.author}`}
                      {today && ` · ${agoPhrase(a.postedAt, today, now)}`}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          )}

          <SyncFooter text={honesty.footer} />
        </div>
      </PullToRefresh>
    </>
  )
}

/**
 * THE RUNWAY — the merged two-week deadline list across every course, grouped by
 * day, with OVERDUE always its own group and always first. The grouping is
 * components/school/derive.ts's, unchanged: one derivation, so the phone and the
 * desktop can never disagree about what is overdue.
 */
function RunwayCard({
  today,
  nowMinutes,
  assignments,
  isPending,
  staleFrom,
  overdue,
}: {
  today: ISODate | undefined
  nowMinutes: number
  assignments: ReturnType<typeof useAssignments>['data']
  isPending: boolean
  staleFrom: string | null
  overdue: number
}) {
  if (isPending) return <Skeleton height={200} className="rounded-context" />
  if (!today) return null

  const groups = runwayGroups(assignments ?? [], today, nowMinutes)
  const undated = (assignments ?? []).filter((a) => a.dueAt === null).length

  return (
    <Card className={clsx('gap-[11px] px-[15px]', staleFrom && 'opacity-[0.72]')}>
      {staleFrom && (
        <span className="self-start rounded-pill bg-fill-2 px-2 py-[2px] text-[9.5px] font-semibold uppercase leading-[1.5] tracking-[0.05em] text-ink-3">
          from {staleFrom}
        </span>
      )}

      {groups.length === 0 && (
        <p className="text-[11.5px] leading-[1.45] text-microcopy text-pretty">
          Nothing is dated in the next two weeks.
          {undated > 0 &&
            ` ${undated} ${undated === 1 ? 'assignment carries' : 'assignments carry'} no due date in Canvas — they are on their course pages, which is where a thing with no date can still be found.`}
        </p>
      )}

      {groups.map((group) => (
        <div key={group.key} className="flex flex-col gap-[11px]">
          <div className="mt-[2px] flex items-center gap-[9px]">
            <span
              className={clsx(
                'text-[10px] font-bold uppercase leading-none tracking-[0.07em]',
                group.overdue ? 'text-danger-2' : 'text-ink-2',
              )}
            >
              {group.label}
            </span>
            <span
              className={clsx('h-px flex-1', group.overdue ? 'bg-danger-line' : 'bg-line')}
              aria-hidden
            />
          </div>

          {group.items.map((a) => {
            const t = timeRemaining(a.dueAt, today, nowMinutes)
            return (
              <div
                key={a.id}
                className={clsx(
                  'flex gap-[11px] rounded-tile border-l-[3px] px-3 py-[11px]',
                  group.overdue
                    ? 'border-l-danger-dot bg-danger-fill'
                    : ['bg-surface-2', hueBorderLeft(a.courseCode)],
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[14px] font-semibold leading-[1.25] text-ink text-pretty">
                    {a.title}
                  </div>
                  <div
                    className={clsx(
                      'mt-[5px] text-[11px] leading-none',
                      group.overdue ? 'font-medium text-danger-2' : 'font-normal text-muted',
                    )}
                  >
                    {a.courseCode} · {group.overdue ? 'was due' : 'due'}{' '}
                    {relativeDay(localDayOf(a.dueAt), today)}
                  </div>
                </div>
                <span
                  className={clsx(
                    'flex-none whitespace-nowrap text-[12px] leading-none',
                    t.tone === 'overdue'
                      ? 'font-bold text-danger-2'
                      : t.tone === 'urgent'
                        ? 'font-bold text-urgent-time'
                        : 'font-semibold text-muted',
                  )}
                >
                  {t.label}
                </span>
              </div>
            )
          })}
        </div>
      ))}

      {overdue > 0 && groups.length > 0 && (
        <span className="sr-only">{overdue} overdue, pinned to the top of this list.</span>
      )}
    </Card>
  )
}

/** The 3px left rule in the course's own hue. Written out — the scanner cannot read a template. */
const HUE_BORDER_LEFT: Record<string, string> = {
  'bg-school': 'border-l-school',
  'bg-learning': 'border-l-learning',
  'bg-interests': 'border-l-interests',
  'bg-tasks': 'border-l-tasks',
}
function hueBorderLeft(code: string): string {
  return HUE_BORDER_LEFT[hueBg(code)] ?? 'border-l-school'
}

function CourseRow({
  course,
  today,
  nowMinutes,
  term,
  onOpen,
}: {
  course: Course
  today: ISODate | undefined
  nowMinutes: number
  term: ReturnType<typeof useTerm>['data']
  onOpen: () => void
}) {
  const grade = courseGrade(course)
  const meeting = today ? nextMeeting(course, term, today, nowMinutes) : null

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex flex-none items-center gap-3 rounded-card border border-line bg-surface px-[14px] py-3 text-left active:bg-surface-2"
    >
      <span
        className={clsx('min-h-[38px] w-[5px] flex-none self-stretch rounded-[3px]', hueBg(course.code))}
        aria-hidden
      />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2">
          <span className="truncate text-[14.5px] font-semibold leading-[1.1] text-ink">
            {course.code}
          </span>
          {course.unreadAnnouncements > 0 ? (
            <span className="flex-none rounded-pill bg-danger-line-2 px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.5] text-danger-dot">
              {course.unreadAnnouncements} unread
            </span>
          ) : grade.kind === 'score' ? (
            <span className="flex-none rounded-pill bg-success-fill px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.5] text-success">
              {gradeChipLabel(grade)}
            </span>
          ) : null}
          {/* A HIDDEN GRADE GETS NO PILL HERE and no blank one either — the pill
              slot is for a fact, and "hidden" is a sentence, which the course
              page states in full. */}
        </span>
        <span className="mt-[5px] block truncate text-[12px] font-normal leading-[1.4] text-ink-3">
          {course.name}
          {meeting && today && ` · next ${minutesToClock(meeting.startMinutes)} ${relativeDay(meeting.date as ISODate, today)}`}
        </span>
      </span>
      <MChevronRight className="flex-none text-faintest" />
    </button>
  )
}
